import { MessageDescriptor } from 'react-intl';

import {
  EffectProductStateRemoveReasonCode,
  Order,
  SubscriptionReplacementCause,
} from 'protocol/api/billing_new/dto_order_new_pb';
import { ProductState } from 'protocol/api/billing_new/dto_product_new_pb';

import { i18n as billingI18n } from '@/entities/billing';

export type OrderRemovalInfo = {
  productStateID: string;
  reason?: MessageDescriptor;
  comment?: string;
};

export const getReplacementCauseMessage = (
  order?: Order,
): MessageDescriptor | undefined => {
  const effect = order?.Effect?.Effect;

  if (effect?.case !== 'SubscriptionReplacementActivate') {
    return undefined;
  }

  const { Cause } = effect.value;

  return Cause !==
    SubscriptionReplacementCause.SubscriptionReplacementCause_InvalidValue
    ? billingI18n.subscriptionReplacementCauseMessages[Cause]
    : undefined;
};

// A receipt reports a removal only when its own trigger closed the
// subscription; on its other causes something else closed it and recorded that
// removal itself, so counting it here would show the closure twice.
export const getOrderRemovalInfo = (
  order?: Order,
): OrderRemovalInfo | undefined => {
  const effect = order?.Effect?.Effect;

  if (effect?.case === 'ProductStateRemove') {
    const { RemoveProductStateID, Reason, Comment } = effect.value;

    if (!RemoveProductStateID) {
      return undefined;
    }

    return {
      productStateID: RemoveProductStateID,
      reason:
        Reason !==
        EffectProductStateRemoveReasonCode.EffectProductStateRemoveReasonCode_InvalidValue
          ? billingI18n.removeProductReasonMessages[Reason]
          : undefined,
      comment: Comment || undefined,
    };
  }

  if (effect?.case === 'SubscriptionReplacementActivate') {
    const { RemovedProductStateID } = effect.value;

    if (!RemovedProductStateID) {
      return undefined;
    }

    return {
      productStateID: RemovedProductStateID,
      reason: getReplacementCauseMessage(order),
    };
  }

  return undefined;
};

export const getOrderRemovedSubscriptionName = (
  order: Order,
  products: ProductState[],
): string => {
  const removalInfo = getOrderRemovalInfo(order);

  if (!removalInfo) {
    return '';
  }

  return (
    products?.find(({ ID }) => ID === removalInfo.productStateID)?.Model
      ?.Name ?? ''
  );
};
